import TitleWithSubtitleCell from 'components/table/TitleWithSubtitleCell'
import StatusCell from 'components/table/StatusCell'
import { statusChecker } from 'components/StatusMessage/Status'
import { getFormattedDate, getTypeKey } from 'utils'
import { unknown } from 'utils/constants'
import type { ExtractElementType } from 'utils/utils.d'
import type { v5 } from '@contracts'
import type { ColDef } from 'ag-grid-community'
import latestGpcVersion from '@cli/gpc/FE_GPC_2024_12_2-converted-20250401-1743497076125-level_4.json'
import { WppIconWarning, WppTypography } from '@wppopen/components-library-react'
import styles from './RenderSelectedProjectRegion.module.scss'
import clsx from 'clsx'

type SingleProduct = ExtractElementType<v5.ProductsListData['products']>
type SingleProductValidationStatusType = NonNullable<SingleProduct['validation']>['status']

const categoryCellRenderer = ({ data }: { data: SingleProduct }) => {
  const gpcCode = latestGpcVersion.find(gpc => gpc.Code === data.gpc_code)
  if (!gpcCode)
    return (
      <WppTypography type="s-body" className={styles.categoryCell__assigning} data-testid="gpc-code-assigning">
        Assigning
      </WppTypography>
    )

  if (gpcCode.Code === 99999999) {
    return (
      <div className={styles.categoryCell__error} data-testid="gpc-code-assignment-error-container">
        <WppIconWarning slot="icon-end" aria-label="Warning icon" />
        <WppTypography type="s-body">Error assigning</WppTypography>
      </div>
    )
  }
  return (
    <TitleWithSubtitleCell
      title={
        <>
          <WppTypography
            className={clsx(styles.categoryCell__title, styles.categoryCell__code)}
            type="s-body"
            data-testid="gpc-code"
          >
            {gpcCode.Code}
          </WppTypography>{' '}
          {gpcCode.Title}
        </>
      }
      subTitle={gpcCode.Definition || ''}
    />
  )
}

export const columnStructureDraftRegion: ColDef<SingleProduct>[] = [
  {
    headerName: 'Product',
    field: getTypeKey<SingleProduct>('reference_name'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => {
      return <TitleWithSubtitleCell title={data.reference_name || unknown} subTitle={data.product_id || unknown} />
    },
  },
  {
    headerName: 'Category',
    field: getTypeKey<SingleProduct>('gpc_code'),
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: categoryCellRenderer,
  },
  {
    headerName: 'Variants',
    field: getTypeKey<SingleProduct>('variants'),
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => {
      const variantCount = data.variants?.length || 0
      return variantCount
    },
  },
  {
    headerName: 'Standard Content',
    field: getTypeKey<SingleProduct>('standard_content'),
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => {
      const { standard_content, variants } = data
      const variantCount = variants?.length || 0
      const standardContentCount = standard_content?.length || 0
      const labels = ['Manually created', 'AI generated']

      // If the product has NO variants:
      // If has standard_content = Manually Created
      // Otherwise AI generated.

      // If the product has variants
      // If the variant that has `master` set to true AND has standard_content = Manually Created
      // - (filter variants for 'master' = true and check if standard_content length more than 0)
      // Otherwise AI generated

      if (variantCount === 0) {
        return standardContentCount ? labels[0] : labels[1]
      } else {
        const masterVariant = variants?.find(variant => variant.master && variant.master === true)
        return !!masterVariant && !!masterVariant.standard_content?.length ? labels[0] : labels[1]
      }
    },
  },
  {
    headerName: 'Last updated',
    field: getTypeKey<SingleProduct>('updated_at'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => (
      <TitleWithSubtitleCell
        title={getFormattedDate(data.updated_at)}
        subTitle={data?.update_info?.user_name || unknown}
      />
    ),
  },
  {
    headerName: 'Quality',
    field: 'quality',
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => {
      return renderStatus(data.variants, data.validation?.status)
    },
  },
]

export const columnStructureGeneratedRegion = (numberOfRetailers: number): ColDef<SingleProduct>[] => [
  {
    headerName: 'Product',
    field: getTypeKey<SingleProduct>('reference_name'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => {
      return <TitleWithSubtitleCell title={data.reference_name || unknown} subTitle={data.product_id || unknown} />
    },
  },
  {
    headerName: 'Category',
    field: getTypeKey<SingleProduct>('gpc_code'),
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: categoryCellRenderer,
  },
  {
    headerName: 'Variants',
    field: getTypeKey<SingleProduct>('variants'),
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => {
      const variantCount = data.variants?.length || 0
      return variantCount
    },
  },
  {
    headerName: 'Tasks',
    field: getTypeKey<SingleProduct>('standard_content'),
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => {
      const product = 1
      const numberOfVariant = data.variants?.length || 0

      // if there is variants
      // Variant count: 3
      // Selected retailers: 2
      // Total job count: (3 × 2) + 1 = 7
      const totalWithVariants = numberOfVariant * numberOfRetailers + product

      // if there is no variants
      // Variant count: 0
      // Selected retailers: 3
      // Total job count: 3 + 1 = 4
      const totalWithoutVariants = numberOfRetailers + product

      return `${numberOfVariant === 0 ? totalWithoutVariants : totalWithVariants}`
    },
  },
  {
    headerName: 'Last updated',
    field: getTypeKey<SingleProduct>('updated_at'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => (
      <TitleWithSubtitleCell
        title={getFormattedDate(data.updated_at)}
        subTitle={data?.update_info?.user_name || unknown}
      />
    ),
  },
  {
    headerName: 'Status',
    field: getTypeKey<SingleProduct>('status'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProduct }) => <StatusCell status={data.status} />,
  },
]

export const hasQualityIncomplete = (variants: SingleProduct['variants']) => {
  const statusList = variants?.map(variant => variant.validation?.status) || []
  const hasIncomplete = statusList.some(status => status !== 'complete')

  return hasIncomplete
}

export const renderStatus = (data: SingleProduct['variants'], productStatus: SingleProductValidationStatusType) => {
  const variants = data || []
  // if variant is empty give the product status or the product status is 'incomplete' then return the product status
  // if the product status is 'complete' and the variant is empty then return 'complete'
  // if the product status is 'complete' and the variant is not empty then check if any of the variant has incomplete status then return 'incomplete' otherwise return 'complete'

  if (!variants.length || productStatus === 'incomplete') {
    return statusChecker(productStatus).tag
  }
  const hasIncomplete = hasQualityIncomplete(variants)
  const quality = hasIncomplete ? 'incomplete' : 'complete'
  return statusChecker(quality).tag
}
